import { computed, ref, shallowRef } from "vue";
import * as pdfjs from "pdfjs-dist";
import { Facade } from "@/drawing";
import { drawingImageRepository } from "@/repositories/drawingImage.repository";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import { trackTab } from "@/analytics/drawing-analytics";
import { normalizeImageOrientation } from "@/utils/imageOrientation";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.mjs",
  import.meta.url,
).toString();

/** Images en cours d'insertion (plusieurs photos validées d'un coup). */
const imageInsertsInProgress = ref(0);

/**
 * Une image est en cours d'ajout sur le plan. Partagé : l'outil Image est monté
 * à deux endroits, l'indicateur une seule fois (cf. ImageInsertingIndicator).
 */
export const isInsertingImage = computed(
  () => imageInsertsInProgress.value > 0,
);

/** Attend que le navigateur ait peint l'état courant (deux frames). */
function nextPaint(): Promise<void> {
  return new Promise((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );
}

/**
 * Insertion d'images et de pages PDF sur le plan (outil Image de la barre
 * d'outils, ex-onglet Images du volet droit).
 */
export function useImageImport() {
  /** Caméra intégrée ouverte. */
  const showCameraOverlay = ref(false);
  /** L'appareil a au moins une caméra (webcam, caméra de la tablette). */
  const hasCamera = ref(false);

  // shallowRef prevents Vue from deep-proxying the pdfjs object,
  // which would break access to its private class fields (#private).
  const pdfDocument = shallowRef<any>(null);

  /**
   * Lit, remet à l'endroit et pose l'image sur le plan. L'indicateur reste
   * affiché jusqu'à l'apparition de l'image, pas pendant l'enregistrement
   * local qui suit.
   */
  async function insertImage(file: File, source: "gallery" | "camera") {
    let added: {
      rasterPath: string;
      data: Uint8Array<ArrayBuffer>;
      stored: File;
    } | null = null;
    imageInsertsInProgress.value++;
    try {
      // L'insertion ODA bloque le fil principal : on laisse l'indicateur
      // s'afficher avant.
      await nextPaint();
      if (!Facade.isInitialized) return;
      // ODA (et AutoCAD) ignorent l'orientation EXIF : on l'applique aux pixels,
      // sinon une photo de l'appareil photo du système arrive pivotée.
      const normalized = await normalizeImageOrientation(
        new Uint8Array(await file.arrayBuffer()),
        file.type,
      );
      const data = normalized.bytes as Uint8Array<ArrayBuffer>;
      const stored = normalized.changed
        ? new File([data], file.name, { type: "image/jpeg" })
        : file;
      if (!Facade.isInitialized) return;
      const rasterPath = Facade.images.add(file.name, data);
      if (rasterPath) added = { rasterPath, data, stored };
    } catch (err) {
      console.error("[useImageImport] Failed to insert image:", err);
    } finally {
      imageInsertsInProgress.value--;
    }
    if (!added) return;

    trackTab("images", "image_imported", { source });
    try {
      await drawingImageRepository.appendNewImageOnDrawing(
        CanvasSettingsState.drawingId,
        added.rasterPath,
        added.data,
        added.stored,
      );
    } catch (err) {
      console.error("[useImageImport] Failed to persist image:", err);
    }
  }

  /** `@change` de l'`<input type="file">` d'image de l'appareil. */
  function onImageInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    void insertImage(input.files[0], "gallery");
    input.value = "";
  }

  /**
   * Caméras présentes ? Lisible sans autorisation (seuls les noms des appareils
   * en ont besoin). Sans caméra (ou hors contexte sécurisé), « Prendre une
   * photo » est masqué : il ferait la même chose qu'« Image depuis l'appareil ».
   */
  async function refreshCameraAvailability() {
    try {
      const devices =
        (await navigator.mediaDevices?.enumerateDevices?.()) ?? [];
      hasCamera.value = devices.some((d) => d.kind === "videoinput");
    } catch {
      hasCamera.value = false;
    }
  }

  /**
   * La photo est prise DANS l'app (getUserMedia, même composant que les
   * observations terrain), sur tablette comme sur ordinateur (webcam).
   * L'`<input capture>` d'avant ouvrait l'app appareil photo du système sur
   * tablette : Android tuait alors l'onglet (moteur WASM + dessin en mémoire)
   * et Chrome relançait la page de zéro au retour, sans jamais recevoir le
   * fichier. Sur ordinateur, il ouvrait simplement le sélecteur de fichiers.
   */
  function openCamera() {
    showCameraOverlay.value = true;
  }

  function onCameraCaptured(files: File[]) {
    showCameraOverlay.value = false;
    for (const file of files) void insertImage(file, "camera");
  }

  /** `@change` de l'input PDF : ouvre le choix des pages. */
  async function onPdfInputChange(e: Event) {
    const input = e.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = "";

    const arrayBuffer = await file.arrayBuffer();
    pdfDocument.value = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  }

  function closePdfPicker() {
    pdfDocument.value = null;
  }

  return {
    showCameraOverlay,
    hasCamera,
    pdfDocument,
    onImageInputChange,
    refreshCameraAvailability,
    openCamera,
    onCameraCaptured,
    onPdfInputChange,
    closePdfPicker,
  };
}
